/* eslint-disable indent */
import { DefineOwnModule, RootActions, RootMutations } from 'stores/type';

import defer from 'lodash/defer';
import { generateId } from 'helpers/id';
import { LIBRARY_ID } from 'constants/drag';
import { DEFAULT_ELEMENT_ID } from 'constants/misc';
import EventsManager from 'managers/events.manager';
import DropManager from 'managers/drop.manager';
import UiManager from 'managers/ui.manager';
import { State, Getters, Actions, Mutations } from './types';

const moduleState: State = {
  dragging: null,
  over: null,
  listening: [],
  static: {
    [LIBRARY_ID]: {
      id: LIBRARY_ID,
      dimensions: { width: 0, height: 0 },
      position: { x: 0, y: 0 },
    },
  },
  draggableHelper: null,
};

const storeModule: DefineOwnModule<
  State,
  Getters,
  Mutations & Pick<RootMutations, 'WORKSPACE_ELEMENT_REMOVE'>,
  Actions & Pick<RootActions, 'baseLoaded'>
> = {
  state: moduleState,

  getters: {
    draggedId: (state) => state.dragging,
    draggedElement: (state, getters) => {
      if (!state.dragging) {
        return null;
      }

      return getters.droppables.find((e) => e.id === state.dragging) || null;
    },
    isDragging: (state) => state.dragging !== null,


    draggingOverId: (state) => state.over,
    draggingOverElement: (state, getters) => {
      const over = state.over;
      if (!over) {
        return null;
      }

      return getters.droppableById(over);
    },

    isOver: (state) => state.over !== null,
    isOverStatic: (_state, getters) => {
      const element = getters.draggingOverElement;
      return !!element && element.hasOwnProperty('dimensions');
    },


    draggableHelperId: (state) => state.draggableHelper,
    draggableHelperElement: (state, getters) => {
      if (!state.draggableHelper) {
        return null;
      }

      return getters.droppableById(state.draggableHelper);
    },


    droppables: (_state, getters) => getters.workspaceElements,
    droppablesActive: (state, getters) =>
      getters.droppables.filter((e) => e.id !== state.draggableHelper),
    droppablesListening: (state, getters) =>
      getters.droppables.filter((e) => state.listening.includes(e.id)),
    droppablesStatic: (state) => state.static,
    droppableStaticById: (state) => (id) => state.static[id],
    droppablesToListen: (_state, getters) => (draggingId) =>
      getters.droppables
        .filter(({ id }) => id !== draggingId && id !== getters.draggableHelperId)
        .map(({ id }) => id),

    droppableById: (state, getters) => (id) => {
      const element = getters.droppables.find((e) => e.id === id);
      if (element) {
        return element;
      }

      if (state.static.hasOwnProperty(id)) {
        return state.static[id];
      }

      return null;
    },
  },

  actions: {
    dragStart({ commit, dispatch, getters }, id) {
      const listening = getters.droppablesToListen(id);

      commit('DRAG_START', { id, listening });
      EventsManager.emit('DRAG_START');

      DropManager.onDragStart();
      dispatch('mixReset');

      defer(() => {
        dispatch('searchHide');
        UiManager.onDragStart();
      });
    },
    dragOver({ commit }, id) {
      commit('DRAG_OVER', id);
    },
    dragOut({ commit }) {
      commit('DRAG_OUT');
    },
    dragEnd({ commit, dispatch, getters }, { position, simulated }) {
      commit('DRAG_POSITION', {
        dragged: getters.draggedElement!,
        ...position,
      });

      DropManager.onDragEnd(simulated);

      commit('DRAG_END');
      defer(() => {
        dispatch('draggableHelperAdd', true);
        UiManager.onDragEnd();
      });
    },

    staticDroppableAdd({ commit }, droppable) {
      commit('STATIC_DROPPABLE_ADD', droppable);
    },

    baseLoaded({ dispatch }) {
      dispatch('draggableHelperAdd', true);
    },
    draggableHelperAdd({ commit, state }, create = false) {
      if (state.draggableHelper !== null) {
        return;
      }

      const id = create ? generateId(DEFAULT_ELEMENT_ID) : null;
      commit('DRAGGABLE_HELPER_SET', id);
    },
  },

  mutations: {
    DRAG_START(state, { id, listening }) {
      state.dragging = id;
      state.listening = listening;
    },
    DRAG_OVER(state, id) {
      state.over = id;
    },
    DRAG_OUT(state) {
      state.over = null;
    },
    DRAG_POSITION(_state, { dragged, x, y }) {
      if (dragged) {
        dragged.position.x = x;
        dragged.position.y = y;
      }
    },
    DRAG_END(state) {
      state.dragging = null;
      state.over = null;
      if (state.listening.length) {
        state.listening = [];
      }
    },

    DROPPABLE_DROP(state) {
      state.over = null;
    },

    DRAGGABLE_HELPER_SET(state, id) {
      if (!id) {
        state.draggableHelper = null;
        return;
      }

      state.draggableHelper = id;
    },
    DRAGGABLE_HELPER_RESET(state) {
      state.draggableHelper = null;
    },

    STATIC_DROPPABLE_ADD(state, droppable) {
      state.static[droppable.id] = droppable;
    },
    STATIC_DROPPABLE_UPDATE(state, { id, width, height, x, y }) {
      const droppable = state.static[id];
      droppable.dimensions.width = width;
      droppable.dimensions.height = height;
      droppable.position.x = x;
      droppable.position.y = y;
    },

    WORKSPACE_ELEMENT_REMOVE(state, id) {
      if (state.dragging === id) {
        state.dragging = null;
      }
    },
  },
};

export default storeModule;
